import React from 'react';
import { ConfiguratorPresetDto } from '@/services/configurator';
import { SelectedIndicator } from './SelectedIndicator';
import { SelectStyleButton } from './SelectStyleButton';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { isDeadlineInPast } from '@/utils/date';
import { Button, SimpleTooltip } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';

export interface StyleButtonProps {
    style: ConfiguratorPresetDto;
    roomId: string | undefined;
    /** The style group has been signed/locked; the server rejects pick/unpick. */
    isLocked?: boolean;
}

export const StyleButton: React.FC<StyleButtonProps> = ({ style, roomId, isLocked }) => {
    const { projectData, selectedDeadlineId, viewOnly, isShowroom } = useOptionConfigurator();
    const selectedDeadline = projectData?.deadlines.find((d) => d.id === selectedDeadlineId);
    // Deadlines are irrelevant in the showroom (anonymous preview): never block style selection on an expired deadline.
    const deadlinePassed = !isShowroom && !!selectedDeadline && isDeadlineInPast(selectedDeadline.deadline);

    if (style.isPicked) {
        return <SelectedIndicator />;
    }

    // A locked style group means its options were sent or the deadline expired — neither applies in
    // the anonymous showroom preview, so don't block style switching there (mirrors `deadlinePassed`).
    if (isLocked && !isShowroom) {
        return (
            <SimpleTooltip content={<Trans>This style group is locked</Trans>}>
                <Button variant="tertiary" size="lg" disabled>
                    <Trans>Select style</Trans>
                </Button>
            </SimpleTooltip>
        );
    }

    if (viewOnly) {
        return (
            <SimpleTooltip content={<Trans>Selections are disabled in this view</Trans>}>
                <Button variant="tertiary" size="lg" disabled>
                    <Trans>Select style</Trans>
                </Button>
            </SimpleTooltip>
        );
    }

    return <SelectStyleButton style={style} roomId={roomId} disabled={deadlinePassed} />;
};
